import { createContext, useState, useEffect, useContext, type ReactNode } from 'react';

/**
 * Preferência por usuário "Fonte de digitação" — família/corpo dos campos
 * onde o médico digita (input, textarea, select, células OD/OE).
 *
 *  normal  — Inter, 13 px (o padrão do app)
 *  narrow  — Roboto Condensed, 13 px: mesma altura, letras mais estreitas
 *  compact — Roboto Condensed, 12 px: estreita E menor, linha mais baixa
 *
 * Persiste em localStorage (mesmo padrão de ThemeContext) e vira o atributo
 * `data-typing="normal|narrow|compact"` no <html>; o CSS global em
 * src/index.css (bloco "Fonte de digitação") faz o resto por seletor.
 */
export type TypingFont = 'normal' | 'narrow' | 'compact';

export const TYPING_FONT_OPTIONS: ReadonlyArray<{
  value: TypingFont;
  label: string;
  hint: string;
}> = [
  { value: 'normal', label: 'Padrão', hint: 'Inter · 13 px · numerais tabulares' },
  { value: 'narrow', label: 'Estreita', hint: 'Roboto Condensed · 13 px · mesma altura' },
  { value: 'compact', label: 'Compacta', hint: 'Roboto Condensed · 12 px · linha mais baixa' },
];

const STORAGE_KEY = 'dratento-typing';

export const isTypingFont = (v: unknown): v is TypingFont =>
  v === 'normal' || v === 'narrow' || v === 'compact';

const readStored = (): TypingFont => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isTypingFont(stored) ? stored : 'normal';
  } catch {
    return 'normal';
  }
};

interface TypingFontContextType {
  typingFont: TypingFont;
  setTypingFont: (font: TypingFont) => void;
}

export const TypingFontContext = createContext<TypingFontContextType>({
  typingFont: 'normal',
  setTypingFont: () => {},
});

export function useTypingFont() {
  return useContext(TypingFontContext);
}

export function TypingFontProvider({ children }: { children: ReactNode }) {
  const [typingFont, setTypingFontState] = useState<TypingFont>(readStored);

  useEffect(() => {
    document.documentElement.setAttribute('data-typing', typingFont);
    try {
      localStorage.setItem(STORAGE_KEY, typingFont);
    } catch {
      /* armazenamento indisponível (modo privado) — a escolha vale só na sessão */
    }
  }, [typingFont]);

  // Outra aba — ou outro computador, pela conta (prefsNaConta) — trocou a fonte.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && isTypingFont(e.newValue)) setTypingFontState(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTypingFont = (f: TypingFont) => setTypingFontState(f);

  return (
    <TypingFontContext.Provider value={{ typingFont, setTypingFont }}>
      {children}
    </TypingFontContext.Provider>
  );
}
